import {
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Loader2
} from "lucide-react";
import React, { useState } from "react";
import { EDSButton } from "../../../eds/EDSButton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from "../../../ui/dialog";

export type LoadingStage =
  | "authentication"
  | "organisation"
  | "venues"
  | "venueConfig"
  | "complete";

export type LoadingStatus = "pending" | "loading" | "success" | "error";

export interface StageInfo {
  stage: LoadingStage;
  status: LoadingStatus;
  error?: {
    message: string;
    technicalDetails?: string;
  };
}

interface LoadingStatusModalProps {
  isOpen: boolean;
  stages: StageInfo[];
  onRetry?: () => void;
  onClose?: () => void;
}

const stageLabels: Record<LoadingStage, string> = {
  authentication: "Authenticating user",
  organisation: "Loading organisation",
  venues: "Fetching venue list",
  venueConfig: "Loading venue configuration",
  complete: "Ready"
};

const userFriendlyMessages: Record<LoadingStage, string> = {
  authentication: "We're verifying your credentials and loading your profile.",
  organisation: "We're checking which organisations you have access to.",
  venues: "We're loading the list of venues for your organisation.",
  venueConfig: "We're setting up the venue you selected.",
  complete: "Everything is ready!"
};

export const LoadingStatusModal: React.FC<LoadingStatusModalProps> = ({
  isOpen,
  stages,
  onRetry,
  onClose
}) => {
  const [expandedStages, setExpandedStages] = useState<Set<LoadingStage>>(
    new Set()
  );

  const toggleExpanded = (stage: LoadingStage) => {
    const newExpanded = new Set(expandedStages);
    if (newExpanded.has(stage)) {
      newExpanded.delete(stage);
    } else {
      newExpanded.add(stage);
    }
    setExpandedStages(newExpanded);
  };

  const hasErrors = stages.some((s) => s.status === "error");
  const allComplete = stages.every((s) => s.status === "success");

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose?.()}>
      <DialogContent className="LoadingStatusModal max-w-md">
        <DialogHeader>
          <DialogTitle>
            {hasErrors ? "Loading Error" : allComplete ? "Ready" : "Loading..."}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Application loading status
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-4">
          {stages.map((stageInfo) => {
            const isExpanded = expandedStages.has(stageInfo.stage);
            const hasError = stageInfo.status === "error";

            return (
              <div key={stageInfo.stage} className="space-y-2">
                <div className="flex items-start gap-3">
                  {stageInfo.status === "success" && (
                    <CheckCircle className="w-5 h-5 text-[hsl(var(--success))] mt-0.5 flex-shrink-0" />
                  )}
                  {stageInfo.status === "loading" && (
                    <Loader2 className="w-5 h-5 text-[hsl(var(--info))] animate-spin mt-0.5 flex-shrink-0" />
                  )}
                  {stageInfo.status === "error" && (
                    <AlertCircle className="w-5 h-5 text-destructive mt-0.5 flex-shrink-0" />
                  )}
                  {stageInfo.status === "pending" && (
                    <div className="w-5 h-5 rounded-full border-2 border-border-color mt-0.5 flex-shrink-0" />
                  )}

                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-foreground">
                      {stageLabels[stageInfo.stage]}
                    </p>
                    {stageInfo.status === "loading" && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {userFriendlyMessages[stageInfo.stage]}
                      </p>
                    )}
                    {hasError && stageInfo.error && (
                      <div className="mt-2">
                        <p className="text-sm text-destructive">
                          {stageInfo.error.message}
                        </p>
                        {stageInfo.error.technicalDetails && (
                          <button
                            onClick={() => toggleExpanded(stageInfo.stage)}
                            className="flex items-center gap-1 mt-2 text-xs text-muted-foreground hover:text-foreground"
                          >
                            {isExpanded ? (
                              <ChevronUp className="w-4 h-4" />
                            ) : (
                              <ChevronDown className="w-4 h-4" />
                            )}
                            {isExpanded ? "Hide" : "Show"} technical details
                          </button>
                        )}
                        {isExpanded && stageInfo.error.technicalDetails && (
                          <div className="mt-2 p-3 bg-muted rounded text-xs font-mono text-muted-foreground overflow-auto max-h-32 whitespace-pre-wrap break-words">
                            {stageInfo.error.technicalDetails.length > 2000
                              ? `${stageInfo.error.technicalDetails.substring(0, 2000)}... (truncated)`
                              : stageInfo.error.technicalDetails}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {(hasErrors || allComplete) && (
          <div className="flex justify-end gap-2 pt-2">
            {hasErrors && onRetry && (
              <EDSButton variant="default" onClick={onRetry}>
                Retry
              </EDSButton>
            )}
            {onClose && (
              <EDSButton variant="outline" onClick={onClose}>
                Close
              </EDSButton>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
